import type { CompanyArticleDetailsRedirect } from "@/modules/companies/client/api/endpoints/companyArticleDetails"
import type { CompanyArticleDetails } from "@/modules/companies/client/api/types"

import { useQuery } from "@tanstack/react-query"
import { useParams } from "next/navigation"

import {
  COMPANY_ARTICLE_DETAILS_QUERY_KEY,
  getCompanyArticleDetails,
} from "@/modules/companies/client/api/endpoints/companyArticleDetails"
import { QUERY_CACHE_TIME, QUERY_STALE_TIME } from "@/modules/companies/client/constants/enums"
import { COMPANY_NEWSFEED } from "@/modules/companies/client/utils/routing"
import { useLocale } from "@/modules/locales/useLocale"
import { useRouter } from "@/modules/routing/hooks/useRouter"

export type CompanyArticleDetailsFetch = {
  dataCompanyArticleDetails?: CompanyArticleDetails
  isLoadingCompanyArticleDetails: boolean
  isSuccessCompanyArticleDetails: boolean
  isErrorCompanyArticleDetails: boolean
}

export const useCompanyArticleDetailsFetch = (): CompanyArticleDetailsFetch => {
  const { replace } = useRouter()
  const { companySlug, articleSlug } = useParams()
  const locale = useLocale()

  const {
    data,
    isPending: isLoadingCompanyArticleDetails,
    isSuccess: isSuccessCompanyArticleDetails,
    isError: isErrorCompanyArticleDetails,
  } = useQuery({
    enabled: !!articleSlug,
    gcTime: QUERY_CACHE_TIME,
    queryFn: () => getCompanyArticleDetails({ article_slug: articleSlug as string }),
    queryKey: [COMPANY_ARTICLE_DETAILS_QUERY_KEY, articleSlug],
    staleTime: QUERY_STALE_TIME,
  })

  if (data && (data as CompanyArticleDetailsRedirect).responseType === "redirect") {
    replace(
      `/${locale}/companies/${companySlug}/${COMPANY_NEWSFEED}/${(data as CompanyArticleDetailsRedirect).newPermalink}`
    )
  }

  return {
    dataCompanyArticleDetails: data ? (data as CompanyArticleDetails) : undefined,
    isErrorCompanyArticleDetails,
    isLoadingCompanyArticleDetails,
    isSuccessCompanyArticleDetails,
  }
}
